import type { JOB_ADS_CANDIDACY_TYPES } from "@/modules/jobAds/client/api/constants"
import type { Facet, JobAd, JobAdsResult } from "@/modules/jobAds/client/api/types"
import type {
  JobAdDTO,
  JobAdFacetPropertiesFromAlgolia,
  JobAdFacetValuesFromES,
  LocationDTO,
} from "@/modules/jobAds/types/jobAdFromAlgolia"
import type { Location } from "@/modules/locations/locations"
import type { Hit, MultipleQueriesResponse, SearchResponse } from "@algolia/client-search"

import _uniqBy from "lodash/uniqBy"
import { NIL as NIL_UUID } from "uuid"

import { ALGOLIA_FACETS, ES_FACETS } from "@/modules/jobAds/client/constants"

const computeJobAdLocationFromAlgolia = (location: LocationDTO): Location => {
  return {
    city: location.city || "",
    country: location.country || "",
    formattedAddress: location.formatted_address || "",
    placeId: location.place_id || "",
    state: location.state || "",
    subState: location.sub_state || "",
    ...(location.latitude && { latitude: location.latitude }),
    ...(location.longitude && { longitude: location.longitude }),
  }
}

export const computeJobAdContractFromAlgolia = (jobAdContractFromAlgolia: JobAdDTO["contract"]): JobAd["contract"] => {
  return {
    durationMax: jobAdContractFromAlgolia?.duration_max || null,
    durationMin: jobAdContractFromAlgolia?.duration_min || null,
    type: jobAdContractFromAlgolia?.type.original_label || "",
  }
}

export const computeJobAdFromAlgolia = (
  jobAdFromAlgolia: Hit<JobAdDTO>,
  position: number,
  searchIndex: string
): JobAd => {
  return {
    activationTime: jobAdFromAlgolia.activated_at ? new Date(jobAdFromAlgolia.activated_at * 1000).toISOString() : "",
    candidacyType: (jobAdFromAlgolia.candidacy_type || "UNKNOW") as keyof typeof JOB_ADS_CANDIDACY_TYPES,
    company: {
      id: jobAdFromAlgolia.company?.id || NIL_UUID,
      logoUrl: jobAdFromAlgolia.company?.logo_url || "",
      name: jobAdFromAlgolia.company?.name || "",
    },
    contract: computeJobAdContractFromAlgolia(jobAdFromAlgolia.contract),
    createdByStudentOrAlumni: Boolean(jobAdFromAlgolia.created_by_student_or_alumni),
    exclusive: Boolean(jobAdFromAlgolia.exclusive),
    id: jobAdFromAlgolia.uuid || NIL_UUID,
    legacyPartnerSchoolIds: jobAdFromAlgolia.partner_school_uuids || [],
    location:
      jobAdFromAlgolia.locations && jobAdFromAlgolia.locations.length && jobAdFromAlgolia.locations[0]
        ? computeJobAdLocationFromAlgolia(jobAdFromAlgolia.locations[0])
        : null,
    locations: jobAdFromAlgolia.locations?.filter(Boolean).map(computeJobAdLocationFromAlgolia) || [],
    position,
    premium: Boolean(jobAdFromAlgolia.premium),
    privateOffer: Boolean(jobAdFromAlgolia.private_offer),
    publicationType: jobAdFromAlgolia.publication_type
      ? {
          name: jobAdFromAlgolia.publication_type.name || "",
          score: jobAdFromAlgolia.publication_type.score,
        }
      : null,
    searchIndex,
    slug: jobAdFromAlgolia.slug || "",
    startPeriod: jobAdFromAlgolia.start_date || "",
    title: jobAdFromAlgolia.title || "",
  }
}

const isJobAdFacetKeyFromES = (value: string): value is JobAdFacetValuesFromES => {
  return ES_FACETS.includes(value)
}

export const computeJobAdFacetsFromAlgolia = (facetResults: SearchResponse<JobAdDTO>[]): Facet[] => {
  if (!facetResults) return []

  const algoliaFacetsKeys = Object.keys(ALGOLIA_FACETS) as JobAdFacetPropertiesFromAlgolia[]

  return algoliaFacetsKeys.flatMap((algoliaFacetKey, index) => {
    const facetParentName = algoliaFacetKey.toLowerCase()

    const esKeyFromAlgolia = isJobAdFacetKeyFromES(facetParentName)

    if (esKeyFromAlgolia) {
      const facetResult = facetResults[index]

      const algoliaFacetValues = facetResult?.facets && Object.assign({}, ...Object.values(facetResult.facets))

      if (!algoliaFacetValues) return []

      const values = Object.entries(algoliaFacetValues).filter(([value]) => {
        if (algoliaFacetKey === "JOB_WITH_IMPACT" && value === "false") {
          return false
        }

        return true
      })

      return values.map(([value, count]) => {
        let name = value

        if (algoliaFacetKey === "JOB_WITH_IMPACT") {
          name = "job_with_impact"
        }

        return {
          count,
          name,
          parentName: facetParentName,
        }
      }) as Facet[]
    }

    return []
  })
}

const getHitsFromResult = (
  result: SearchResponse<JobAdDTO>,
  { basePosition = 0 }: { basePosition?: number }
): JobAd[] => {
  const hits = result?.hits || []
  const searchIndex = result?.index || ""

  return hits.map((hit, index) => computeJobAdFromAlgolia(hit, basePosition + index, searchIndex))
}

export const computeJobAdsResultFromAlgolia = ({
  response,
  withSavedSearch,
}: {
  response?: MultipleQueriesResponse<JobAdDTO>
  withSavedSearch: boolean
}): JobAdsResult => {
  const jobAdsResults = response?.results
  const mainResults = jobAdsResults?.[1] as SearchResponse<JobAdDTO>
  const searchId = mainResults?.queryID || NIL_UUID
  const page = mainResults?.page || 0
  const algoliaCustomData = mainResults?.userData

  const sponsoredResults = jobAdsResults?.[0] as SearchResponse<JobAdDTO>
  const sponsoredResultsHits = page === 0 ? getHitsFromResult(sponsoredResults, {}) : []

  const mainResultsHits = getHitsFromResult(mainResults, { basePosition: sponsoredResultsHits.length })

  const hits = _uniqBy([...sponsoredResultsHits, ...mainResultsHits], "id")
  const pageSize = hits.length

  const facets = computeJobAdFacetsFromAlgolia(
    jobAdsResults?.slice(2, jobAdsResults.length - (withSavedSearch ? 1 : 0)) as SearchResponse<JobAdDTO>[]
  )

  const savedSearch =
    jobAdsResults && withSavedSearch ? (jobAdsResults[jobAdsResults.length - 1] as SearchResponse<JobAdDTO>) : null

  return {
    ...(algoliaCustomData ? { algoliaCustomData } : []),
    exhaustiveFacets: !!mainResults.exhaustiveFacetsCount,
    exhaustiveResults: mainResults.exhaustiveNbHits,
    facets,
    jobAds: hits,
    pageSize,
    savedSearchSize: savedSearch ? savedSearch.nbHits : 0,
    searchId,
    totalSize: mainResults?.nbHits || 0,
  }
}
